/**
 * Turns the current waypoints into a routed line.
 *
 * Debounced, and every request supersedes the one before it: dragging a marker
 * fires a change per frame, and without cancellation the map would flicker
 * between an old response and a new one depending on which landed last.
 */

import { useEffect, useRef, useState } from 'react';
import { DirectionsClient, type DirectionsResult, type DraftRoute } from '@cad/core';
import { MAPBOX_TOKEN } from './config';

const DEBOUNCE_MS = 350;

export interface RoutingState {
  result: DirectionsResult | null;
  loading: boolean;
  error: string | null;
}

export function useRouting(draft: DraftRoute, routingKey: string): RoutingState {
  const [state, setState] = useState<RoutingState>({ result: null, loading: false, error: null });
  const abortRef = useRef<AbortController | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    const current = draftRef.current;

    if (current.waypoints.length < 2) {
      abortRef.current?.abort();
      setState({ result: null, loading: false, error: null });
      return;
    }

    if (!MAPBOX_TOKEN) {
      setState({ result: null, loading: false, error: 'No Mapbox token configured' });
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState((previous) => ({ ...previous, loading: true, error: null }));

      const client = new DirectionsClient({ accessToken: MAPBOX_TOKEN });
      client
        .route(current.waypoints, current.profile, current.options, { signal: controller.signal })
        .then((result) => {
          if (controller.signal.aborted) return;
          setState({ result, loading: false, error: null });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setState({
            result: null,
            loading: false,
            error: error instanceof Error ? error.message : 'Routing failed',
          });
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [routingKey]);

  return state;
}
